import { useState, type ReactNode } from 'react';
import { MARKET } from '../../shared/market.generated';
import type { UserSnapshot } from '../../shared/reader';
import type { MarketSnapshot, ReserveSnapshot } from '../../shared/types';
import { BRAND } from '../brand';
import { AssetIcon } from '../components/AssetBadge';
import { IconArrowLeft, IconBars, IconCheck, IconExternal, IconOracle, IconPercent, IconPie, IconWallet } from '../components/Icons';
import { useModals } from '../components/modals';
import { PriceChart } from '../components/PriceChart';
import { RateCurve } from '../components/RateCurve';
import { Alert, CapRing, ExternalLink, HintLabel, ReserveTags, TopBand, TopStat } from '../components/ui';
import { usePrices } from '../data/queries';
import { explorerAddress } from '../data/tx';
import { assetMeta } from '../../shared/assets-meta';
import { fmtBps, fmtCompact, fmtMultiplier, fmtPct, fmtToken, fmtUsd, rayToApy, toNumber } from '../lib/format';
import { maxFor, positionOf } from '../lib/position';
import { Change } from './Markets';
import { linkTo } from '../router';
import { useWallet } from '../wallet/WalletProvider';

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className="rsv-section"><h3>{title}</h3><div className="rsv-section__body">{children}</div></div>
);
function Fact({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="fact">
      {hint ? <HintLabel label={label} hint={hint} open={open} onToggle={() => setOpen((v) => !v)} /> : <span>{label}</span>}
      <strong>{children}</strong>
      {hint && open && <small className="hint-text" role="note">{hint}</small>}
    </div>
  );
}
function BoxFact({ label, children, hint }: { label: string; children: ReactNode; hint: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="box-fact">
      <HintLabel label={label} hint={hint} open={open} onToggle={() => setOpen((v) => !v)} />
      <strong>{children}</strong>
      {open && <small className="hint-text" role="note">{hint}</small>}
    </div>
  );
}

function YourInfo({ reserve, user }: { reserve: ReserveSnapshot; user: UserSnapshot | undefined }) {
  const { status } = useWallet();
  const { openAction, openConnect } = useModals();
  if (status !== 'connected') {
    return (
      <aside className="paper your-info"><h2>Your info</h2>
        <p className="muted">Please connect a wallet to view your personal information here.</p>
        <button type="button" className="btn btn--gradient" onClick={openConnect}>Connect wallet</button>
      </aside>
    );
  }
  if (!user) return <aside className="paper your-info"><h2>Your info</h2><div className="skeleton" style={{ height: 140 }} /></aside>;
  const ur = user.reserves[reserve.symbol];
  const position = positionOf(user);
  const toSupply = maxFor('supply', position, reserve, ur).amount;
  const toBorrow = reserve.borrowingEnabled ? maxFor('borrow', position, reserve, ur).amount : 0n;
  const price = Number(reserve.priceUsd) / 1e8;
  const dp = reserve.decimals === 6 ? 2 : 4;
  const blocked = reserve.isPaused || !reserve.isActive;
  return (
    <aside className="paper your-info">
      <h2>Your info</h2>
      <div className="wallet-row"><span className="wallet-row__icon"><IconWallet size={18} /></span><div><small>Wallet balance</small><strong>{fmtToken(ur?.walletBalance ?? 0n, reserve.decimals, dp)} {reserve.symbol}</strong></div></div>
      <div className="info-action">
        <div><small>Available to supply</small><strong>{fmtToken(toSupply, reserve.decimals, dp)} {reserve.symbol}</strong><small>{fmtUsd(toNumber(toSupply, reserve.decimals) * price)}</small></div>
        <button type="button" className="btn btn--primary" disabled={toSupply === 0n || blocked || reserve.isFrozen} onClick={() => openAction('supply', reserve.symbol)}>Supply</button>
      </div>
      {reserve.borrowingEnabled && (
        <div className="info-action">
          <div><small>Available to borrow</small><strong>{fmtToken(toBorrow, reserve.decimals, dp)} {reserve.symbol}</strong><small>{fmtUsd(toNumber(toBorrow, reserve.decimals) * price)}</small></div>
          <button type="button" className="btn btn--primary" disabled={toBorrow === 0n || blocked || reserve.isFrozen} onClick={() => openAction('borrow', reserve.symbol)}>Borrow</button>
        </div>
      )}
      {(ur?.supplied ?? 0n) > 0n && (
        <div className="info-action">
          <div><small>Your supply</small><strong>{fmtToken(ur!.supplied, reserve.decimals, dp)} {reserve.symbol}</strong></div>
          <button type="button" className="btn btn--outlined" disabled={blocked} onClick={() => openAction('withdraw', reserve.symbol)}>Withdraw</button>
        </div>
      )}
      {(ur?.debt ?? 0n) > 0n && (
        <div className="info-action">
          <div><small>Your debt</small><strong>{fmtToken(ur!.debt, reserve.decimals, dp)} {reserve.symbol}</strong></div>
          <button type="button" className="btn btn--outlined" disabled={blocked} onClick={() => openAction('repay', reserve.symbol)}>Repay</button>
        </div>
      )}
      {(ur?.walletBalance ?? 0n) === 0n && <Alert tone="warning" action={<a className="btn btn--outlined btn--sm" {...linkTo('/faucet')}>Faucet</a>}>Your wallet balance of {reserve.symbol} is 0.</Alert>}
      {reserve.borrowingEnabled && position.collateralUsd <= 0 && <Alert>To borrow you need to supply any asset to be used as collateral.</Alert>}
    </aside>
  );
}

export function Reserve({ market, symbol, user }: { market: MarketSnapshot; symbol: string; user: UserSnapshot | undefined }) {
  const [range, setRange] = useState<'1d' | '7d' | '30d'>('7d');
  const history = usePrices(range);
  const { watchAsset, status, walletKind } = useWallet();
  const reserve = market.reserves.find((r) => r.symbol === symbol);
  if (!reserve) {
    return <><TopBand title={<h1>Reserve not found</h1>} /><main className="page"><section className="paper"><p className="no-data">There is no reserve called {symbol} in this market. <a {...linkTo('/markets')}>Back to markets</a></p></section></main></>;
  }
  const cfg = MARKET.assets.find((a) => a.symbol === symbol)!;
  const price = Number(reserve.priceUsd) / 1e8;
  const supplied = toNumber(reserve.totalSupplied, reserve.decimals), debt = toNumber(reserve.totalDebt, reserve.decimals);
  const liquidity = toNumber(reserve.availableLiquidity, reserve.decimals);
  const utilization = supplied > 0 ? Math.min(1, debt / supplied) : 0;
  const supplyCap = Number(reserve.supplyCap), borrowCap = Number(reserve.borrowCap);
  const series = history.data?.series[symbol];
  const canBeCollateral = reserve.usageAsCollateralEnabled && reserve.liquidationThresholdBps > 0;

  return (
    <>
      <TopBand
        back={<a className="back-link" {...linkTo('/markets')}><IconArrowLeft size={14} />Go back<span className="back-link__market">{BRAND.marketName}</span></a>}
        title={
          <div className="reserve-title">
            <AssetIcon symbol={symbol} size={40} />
            <div><small>{symbol}</small><h1>{assetMeta(symbol).en}</h1></div>
            <span className="reserve-title__links">
              <a className="round-link" href={explorerAddress(reserve.address)} target="_blank" rel="noopener noreferrer" title="View token contract" aria-label="View token contract"><IconExternal size={14} /></a>
              {status === 'connected' && walletKind !== 'embedded' && <button type="button" className="round-link" title="Add token to wallet" aria-label="Add token to wallet" onClick={() => void watchAsset({ address: reserve.address, symbol, decimals: reserve.decimals }).catch(() => undefined)}><IconWallet size={14} /></button>}
            </span>
            <ReserveTags reserve={reserve} series={series} explain />
          </div>
        }>
        <TopStat icon={<IconPie size={18} />} label="Reserve size">{fmtUsd(supplied * price, { compact: true })}</TopStat>
        <TopStat icon={<IconBars size={18} />} label="Available liquidity">{fmtUsd(liquidity * price, { compact: true })}</TopStat>
        <TopStat icon={<IconPercent size={18} />} label="Utilization rate">{reserve.borrowingEnabled ? fmtPct(utilization) : '—'}</TopStat>
        <TopStat icon={<IconOracle size={18} />} label="Oracle price">{reserve.priceError ? '—' : fmtUsd(price)}<a className="round-link round-link--sm" href={explorerAddress(cfg.adapter)} target="_blank" rel="noopener noreferrer" title="View oracle contract" aria-label="View oracle contract"><IconExternal size={11} /></a></TopStat>
      </TopBand>

      <main className="page">
        <div className="reserve-grid">
          <section className="paper rsv">
            <h2>Reserve status &amp; configuration</h2>
            {reserve.isPaused && <Alert tone="critical">This reserve is paused: no actions are possible until it is unpaused.</Alert>}
            {reserve.isFrozen && !reserve.isPaused && <Alert tone="warning">This reserve is frozen: existing positions can be repaid and withdrawn, new supplies and borrows are not accepted.</Alert>}
            {reserve.tokenPaused && <Alert tone="critical">The stock token itself is paused by its issuer: token transfers revert, so supplies, withdrawals and liquidations of this reserve fail until it is unpaused.</Alert>}
            {reserve.oracle.oraclePaused && <Alert tone="warning">The token issuer has paused the oracle (corporate action in progress): the feed keeps its last price. Until the new multiplier is effective the admin should keep max LTV at 0 for this stock.</Alert>}

            <Section title="Supply info">
              <div className="rsv-head">
                <CapRing used={supplied} cap={supplyCap} label="Supply cap" />
                <Fact label="Total supplied" hint="Asset supply is limited to a certain amount to reduce protocol exposure to the asset and to help manage risks involved.">
                  {fmtCompact(supplied)} <span className="of">of</span> {supplyCap > 0 ? fmtCompact(supplyCap) : '∞'}
                  <small>{fmtUsd(supplied * price, { compact: true })} of {supplyCap > 0 ? fmtUsd(supplyCap * price, { compact: true }) : '∞'}</small>
                </Fact>
                <Fact label="APY">{fmtPct(rayToApy(reserve.supplyRateRay))}</Fact>
              </div>
              {!reserve.borrowingEnabled && <p className="muted small">This asset is collateral only: nobody can borrow it, so supplying it earns no interest.</p>}
              <div className="collateral-usage">
                <h4>Collateral usage</h4>
                {canBeCollateral ? <span className="text-good inline"><IconCheck size={15} />Can be collateral</span> : <span className="text-warning inline">Asset cannot be used as collateral.</span>}
              </div>
              {canBeCollateral && (
                <div className="box-facts">
                  <BoxFact label="Max LTV" hint="The maximum borrowing power of this collateral. At 35%, every $1 of collateral lets you borrow up to $0.35.">{fmtBps(reserve.ltvBps, 2)}</BoxFact>
                  <BoxFact label="Liquidation threshold" hint="The loan to value at which a position backed by this collateral becomes liquidatable.">{fmtBps(reserve.liquidationThresholdBps, 2)}</BoxFact>
                  <BoxFact label="Liquidation penalty" hint="The bonus liquidators receive on the collateral they seize when they repay a liquidatable debt.">{fmtBps(reserve.liquidationBonusBps, 2)}</BoxFact>
                </div>
              )}
              {reserve.role === 'collateral' && reserve.ltvBps === 0 && <Alert tone="warning">Max LTV is 0 right now, for example because the stock market is closed. This collateral cannot back new borrowing until it is restored. Existing positions keep their liquidation threshold.</Alert>}
            </Section>

            {cfg.faucet === 'stock' && (
              <Section title="Stock token">
                <div className="box-facts">
                  <BoxFact label="Multiplier" hint="ERC-8056 uiMultiplier: how many shares one raw token represents. Splits and dividends change it; raw balances and the pool's accounting never change.">{fmtMultiplier(reserve.oracle.uiMultiplier)}</BoxFact>
                  <BoxFact label="Pending multiplier" hint="A scheduled multiplier and the time it takes effect, mirrored from the mainnet token by the keeper.">{reserve.oracle.pendingEffectiveAt > market.blockTimestamp ? `${fmtMultiplier(reserve.oracle.pendingMultiplier)} from ${new Date(reserve.oracle.pendingEffectiveAt * 1000).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}` : 'none'}</BoxFact>
                  <BoxFact label="Oracle" hint="oraclePaused flag of the token: set by the issuer around corporate actions; the feed then keeps its last price.">{reserve.oracle.oraclePaused ? <span className="text-warning">paused</span> : 'active'}</BoxFact>
                  <BoxFact label="Transfers" hint="Token or registry-wide pause set by the issuer. Supplies, withdrawals and liquidations revert while paused.">{reserve.tokenPaused ? <span className="text-critical">paused</span> : 'active'}</BoxFact>
                </div>
                <p className="muted small">
                  Same contract as the mainnet stock tokens (beacon proxy, ERC-8056 scaled UI amounts, oracle pause, role-gated minting).
                  The oracle price is the value of one raw token: share price times this multiplier. The keeper copies the multiplier and the oracle pause of the mainnet token
                  {cfg.mainnetToken ? <> (<ExternalLink href={`https://robinhoodchain.blockscout.com/address/${cfg.mainnetToken}`}>{cfg.mainnetToken.slice(0, 6)}…{cfg.mainnetToken.slice(-4)}</ExternalLink>)</> : null}
                  {cfg.isin ? <>, ISIN {cfg.isin}</> : null}. <a {...linkTo('/status')}>Corporate actions</a>
                </p>
              </Section>
            )}

            <Section title="Oracle price">
              <div className="chart-head">
                <Fact label={`${symbol} / USD`}>{reserve.priceError ? '—' : fmtUsd(price, { digits: price < 10 ? 4 : 2 })}<small><Change pct={history.data?.series[symbol]?.change24hPct} /> 24h</small></Fact>
                <div className="segmented" role="group" aria-label="Range">
                  {(['1d', '7d', '30d'] as const).map((k) => <button key={k} type="button" aria-pressed={range === k} onClick={() => setRange(k)}>{k.toUpperCase()}</button>)}
                </div>
              </div>
              <div style={{ opacity: history.isFetching ? 0.6 : 1 }}><PriceChart symbol={symbol} points={series?.points ?? []} /></div>
              <p className="muted small">{cfg.source?.type === 'chainlink' ? `Mirrored every 2 minutes from the Chainlink feed on Robinhood Chain mainnet${cfg.source.fallback ? ', or from the Robinhood quote API when the mainnet RPC is unavailable' : ''}.` : cfg.source ? 'Mirrored every 2 minutes from the Robinhood quote API.' : 'No reference source configured.'} <a {...linkTo('/status')}>Keeper status</a></p>
            </Section>

            <Section title="Borrow info">
              {!reserve.borrowingEnabled ? <Alert>Borrowing is disabled for this asset. It can only be supplied as collateral.</Alert> : <>
                <div className="rsv-head">
                  <CapRing used={debt} cap={borrowCap} label="Borrow cap" />
                  <Fact label="Total borrowed" hint="Borrowing of this asset is limited to a certain amount to minimize liquidity pool insolvency.">
                    {fmtCompact(debt)} <span className="of">of</span> {borrowCap > 0 ? fmtCompact(borrowCap) : '∞'}
                    <small>{fmtUsd(debt * price, { compact: true })} of {borrowCap > 0 ? fmtUsd(borrowCap * price, { compact: true }) : '∞'}</small>
                  </Fact>
                  <Fact label="APY">{fmtPct(rayToApy(reserve.borrowRateRay))}</Fact>
                  <Fact label="Borrow cap">{borrowCap > 0 ? fmtCompact(borrowCap) : '∞'}<small>{borrowCap > 0 ? fmtUsd(borrowCap * price, { compact: true }) : ''}</small></Fact>
                </div>
                <div className="collateral-usage"><h4>Collector info</h4></div>
                <div className="box-facts">
                  <BoxFact label="Reserve factor" hint="The share of the interest paid by borrowers that goes to the protocol treasury. The rest is paid to suppliers.">{fmtBps(reserve.reserveFactorBps, 2)}</BoxFact>
                  <div className="box-fact"><span>Collector contract</span><strong><ExternalLink href={explorerAddress(MARKET.treasury)}>View contract</ExternalLink></strong></div>
                </div>
              </>}
            </Section>

            {reserve.borrowingEnabled && reserve.rateModel && (
              <Section title="Interest rate model">
                <div className="chart-head">
                  <Fact label="Utilization rate">{fmtPct(utilization)}</Fact>
                  <ExternalLink href={explorerAddress(MARKET.interestRateStrategy)} className="btn btn--outlined btn--sm">Interest rate strategy</ExternalLink>
                </div>
                <RateCurve reserve={reserve} utilization={utilization} />
                <p className="muted small">Rates are shown as APR, the way the rate model defines them. The APY figures elsewhere include per-second compounding, so they are slightly higher at the same utilization.</p>
              </Section>
            )}
          </section>
          <YourInfo reserve={reserve} user={user} />
        </div>
      </main>
    </>
  );
}
